import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { CheckCircle2, Circle, FileText, Building2, Upload, ArrowRight, X, CreditCard, AlertCircle, ExternalLink, HelpCircle, Award, Truck, PlayCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const VideoPlaceholder = ({ title, description }) => (
  <div className="bg-gray-100 rounded-lg p-3 mt-2 flex items-center gap-3 border border-gray-200">
    <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
      <PlayCircle className="h-6 w-6 text-gray-400" />
    </div>
    <div className="min-w-0">
      <p className="text-xs font-medium text-gray-700 truncate">{title}</p>
      <p className="text-xs text-gray-500">{description}</p>
    </div>
  </div>
);

const OnboardingCard = () => {
  const navigate = useNavigate();
  const { user, apiCall } = useAuth();
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    fetchOnboardingStatus();
  }, []);

  const fetchOnboardingStatus = async () => {
    try {
      const response = await apiCall('/api/vendors/onboarding-status');
      setOnboardingStatus(response);
      
      // Check if onboarding is dismissed in localStorage
      const isDismissed = localStorage.getItem('onboarding_dismissed');
      if (isDismissed && response?.completed) {
        setDismissed(true);
      }
    } catch (error) {
      console.error('Failed to fetch onboarding status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('onboarding_dismissed', 'true');
    setDismissed(true);
  };

  if (loading || !onboardingStatus || dismissed) {
    return null;
  }

  // If account is pending approval, still show the card with relevant info
  const isPendingApproval = user?.status === 'pending';
  
  // Check if there are still setup steps remaining (shipping) even if core onboarding is "complete"
  const hasSetupStepsRemaining = !onboardingStatus.shipping_configured;
  
  // Only hide if truly everything is done AND approved
  if (onboardingStatus.completed && !isPendingApproval && !hasSetupStepsRemaining) {
    return null;
  }

  // Get current tier from API response
  const currentTier = onboardingStatus.current_tier || onboardingStatus.tax_compliance_tier || 'tier_0';
  
  // Tier status helpers — must match backend vendor_onboarding.py tier strings exactly
  const isTier0 = currentTier === 'tier_0';
  const isTier1 = currentTier === 'tier_1_pan_only';
  const isTier2 = currentTier === 'tier_2_gst_enrollment';
  const isTier3 = currentTier === 'tier_3_full_gst';
  const isTier4 = currentTier === 'tier_4_gst_iec';
  const isTier5 = currentTier === 'tier_5_gst_exempt';
  
  // Tax compliance is complete for tier >= 2 (enrollment, full GST, IEC, or exempt)
  const isTaxComplianceComplete = isTier2 || isTier3 || isTier4 || isTier5;
  const needsGSTUpgrade = isTier1; // Only Tier 1 (PAN only) needs upgrade

  // Build dynamic steps based on actual status
  const steps = [];

  // Step 1: Tax Compliance - Only show if not complete OR needs upgrade
  if (!isTaxComplianceComplete) {
    steps.push({
      id: 'tax_compliance',
      label: isTier1 ? 'Upgrade Tax Compliance' : 'Complete Tax Registration',
      description: isTier1 
        ? 'PAN verified ✓ Add Enrollment ID/GST to start transacting.' 
        : 'Add your PAN and tax details to get started',
      icon: Building2,
      completed: false,
      needsUpgrade: isTier1,
      tierBadge: isTier1 ? 'Tier 1 - PAN Only' : null,
      action: () => navigate('/vendor/tax-registration'),
      actionLabel: isTier1 ? 'Upgrade' : 'Start',
      helpLink: 'https://www.aniyor.net/collections/accountant',
      helpLabel: 'Find Tax Consultants'
    });
  }

  // Step 2: Subscription Plan - Only show if NOT selected and NOT skipped during onboarding
  if (!onboardingStatus.subscription_selected && !onboardingStatus.subscription_skipped) {
    steps.push({
      id: 'subscription',
      label: 'Choose Subscription Plan',
      description: 'Select a plan to unlock premium features',
      icon: CreditCard,
      completed: false,
      action: () => navigate('/vendor/subscription-plans')
    });
  }

  // Step 3: Brand Registry Documents - Only show if pending
  if (onboardingStatus.brand_registry_pending) {
    steps.push({
      id: 'brand_documents',
      label: 'Upload Brand Documents',
      description: 'Upload trademark certificate for your registered brand',
      icon: Award,
      completed: false,
      canSkip: true,
      action: () => navigate('/vendor/brand-registry'),
      actionLabel: 'Upload'
    });
  }

  // Step 4: First Listing - Always show if no listings
  if (!onboardingStatus.has_listings) {
    steps.push({
      id: 'first_listing',
      label: 'Create First Listing',
      description: 'Add your first product or service',
      icon: FileText,
      completed: false,
      action: () => navigate('/create-listing')
    });
  }

  // Step 5: KYC Documents - Only show if not uploaded
  if (!onboardingStatus.documents_uploaded) {
    steps.push({
      id: 'documents',
      label: 'Upload KYC Documents',
      description: 'Upload business documents for verification',
      icon: Upload,
      completed: false,
      canSkip: true,
      action: () => navigate('/profile?tab=kyc')
    });
  }

  // Step 6: Shipping Setup - Show if not configured
  if (!onboardingStatus.shipping_configured) {
    steps.push({
      id: 'shipping_setup',
      label: 'Set Up Shipping',
      description: 'Choose your shipping mode and add a pickup location',
      icon: Truck,
      completed: false,
      canSkip: true,
      action: () => navigate('/settings/shipping'),
      actionLabel: 'Setup',
      videoPlaceholder: {
        title: 'How to set up shipping',
        description: 'Learn how to choose shipping mode and add pickup locations'
      }
    });
  }

  // Calculate completion
  const totalSteps = steps.length;
  const completedSteps = steps.filter(s => s.completed).length;
  
  // If no steps to show AND not pending approval, don't render
  // If pending approval, show a special "waiting for approval" card
  if (totalSteps === 0 && !isPendingApproval) {
    return null;
  }
  
  // Special case: Vendor completed setup but waiting for approval
  if (totalSteps === 0 && isPendingApproval) {
    return (
      <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl flex items-center gap-2">
                <span>✅ Setup Complete - Awaiting Approval</span>
              </CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Your vendor account is being reviewed by our team
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleDismiss}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-900">What happens next?</p>
                <ul className="text-sm text-amber-800 mt-2 space-y-1 list-disc list-inside">
                  <li>Our team will review your submitted documents</li>
                  <li>You&apos;ll receive an email once approved</li>
                  <li>Once approved, you can start publishing listings</li>
                </ul>
              </div>
            </div>
          </div>
          
          {/* Show what they can do while waiting */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm font-medium text-blue-900 mb-2">While you wait, you can:</p>
            <div className="flex flex-wrap gap-2">
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => navigate('/create-listing')}
                className="text-blue-700 border-blue-300 hover:bg-blue-100"
              >
                Create Draft Listings
              </Button>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => navigate('/vendor/store-profile')}
                className="text-blue-700 border-blue-300 hover:bg-blue-100"
              >
                Set Up Store Profile
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const progressPercentage = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 100;

  // Show tier badge for completed tax compliance
  const getTierBadge = () => {
    if (isTier5) return { label: 'Tier 5 - GST Exempt', color: 'bg-purple-100 text-purple-800' };
    if (isTier4) return { label: 'Tier 4 - GST + IEC', color: 'bg-emerald-100 text-emerald-800' };
    if (isTier3) return { label: 'Tier 3 - Full GST', color: 'bg-green-100 text-green-800' };
    if (isTier2) return { label: 'Tier 2 - Enrollment', color: 'bg-blue-100 text-blue-800' };
    if (isTier1) return { label: 'Tier 1 - PAN Only', color: 'bg-amber-100 text-amber-800' };
    return null;
  };

  const tierBadgeInfo = getTierBadge();

  return (
    <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl flex items-center gap-2">
              <span>🎯 Complete Your Vendor Setup</span>
              {tierBadgeInfo && isTaxComplianceComplete && (
                <Badge className={`ml-2 ${tierBadgeInfo.color}`}>
                  {tierBadgeInfo.label}
                </Badge>
              )}
            </CardTitle>
            <p className="text-sm text-gray-600 mt-1">
              {totalSteps} step{totalSteps !== 1 ? 's' : ''} remaining
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={handleDismiss}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Progress Bar */}
        <div className="space-y-2">
          <Progress value={progressPercentage} className="h-2" />
        </div>

        {/* Steps List */}
        <div className="space-y-3">
          {steps.map((step) => {
            const Icon = step.icon;
            const needsUpgrade = step.needsUpgrade;
            return (
              <div
                key={step.id}
                className={`flex flex-col p-3 bg-white rounded-lg border transition-all ${
                  needsUpgrade ? 'border-amber-300 bg-amber-50' : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`flex-shrink-0 ${
                      step.completed ? 'text-green-600' : 
                      needsUpgrade ? 'text-amber-600' : 'text-gray-400'
                    }`}>
                      {step.completed ? (
                        <CheckCircle2 className="h-5 w-5" />
                      ) : needsUpgrade ? (
                        <AlertCircle className="h-5 w-5" />
                      ) : (
                        <Circle className="h-5 w-5" />
                      )}
                    </div>
                    <div>
                      <p className={`font-medium text-sm ${
                        step.completed ? 'text-gray-500 line-through' : 'text-gray-900'
                      }`}>
                        {step.label}
                        {step.tierBadge && (
                          <Badge className="ml-2 bg-amber-100 text-amber-800 text-xs">{step.tierBadge}</Badge>
                        )}
                        {step.canSkip && (
                          <span className="ml-2 text-xs text-gray-400">(Optional)</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500">{step.description}</p>
                    </div>
                  </div>
                  {!step.completed && (
                    <Button
                      size="sm"
                      variant={needsUpgrade ? 'default' : 'outline'}
                      onClick={step.action}
                      className={`flex-shrink-0 ${
                        needsUpgrade ? 'bg-amber-500 hover:bg-amber-600' : ''
                      }`}
                    >
                      {step.actionLabel || 'Start'}
                      <ArrowRight className="h-3 w-3 ml-1" />
                    </Button>
                  )}
                </div>
                
                {/* Help link for tax compliance upgrade */}
                {needsUpgrade && step.helpLink && (
                  <div className="mt-2 pt-2 border-t border-amber-200 flex items-center gap-2 text-xs">
                    <HelpCircle className="h-3 w-3 text-amber-600" />
                    <span className="text-amber-700">Need help with GST?</span>
                    <a 
                      href={step.helpLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline flex items-center gap-1"
                    >
                      {step.helpLabel}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                )}
                
                {/* Video placeholder for setup guides */}
                {!step.completed && step.videoPlaceholder && (
                  <VideoPlaceholder 
                    title={step.videoPlaceholder.title}
                    description={step.videoPlaceholder.description}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Tier 3 Complete Message */}
        {isTier3 && steps.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <p className="text-sm text-green-800">
              <strong>✅ GST Verified!</strong> You have full marketplace access. Complete the remaining steps above.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default OnboardingCard;
